import React from "react";

import { InfoOutlined, RedeemOutlined } from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import QuizIcon from "@mui/icons-material/Quiz";
import {
  Box,
  Card,
  Chip,
  IconButton,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import SUGGESTED from "../../assets/icons/suggested.webp";
import AIROBOT from "../../assets/images/robot.png";
import { Button, Tooltip } from "../../common";
import { selectAssessmentById } from "../../redux/slices/assessmentsSlice";
import { setActionButtonToggle as setActionButtonToggleAction } from "../../redux/slices/authSlice";
import { getCamelCaseWord } from "../../utils/functions";
// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: 1,
  justifyContent: "space-between",
  height: "370px",
  overflow: "auto",
  border: "1px solid #D6D6D6",
  "&:hover": {
    boxShadow: theme.shadows[4],
    transition: "box-shadow 0.3s ease-in-out",
  },
}));

const TagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.grey[700],
}));

const AssessmentsCards = ({
  assessment,
  showMyAssessmentBage,
  handleAssessmentSelect,
  handleOpenModal,
  selectedAssessmentId,
  isUserCompanySetup,
  setActionButtonToggle,
}) => {
  const navigate = useNavigate();
  const isProcessing = assessment?.processing_status !== "completed";

  return (
    <>
      {isProcessing ? (
        <Box sx={{ position: "relative" }}>
          {/* Card */}
          <StyledCard
            sx={{
              borderRadius: "10px",
              position: "relative",
              borderTop: "4px solid #FFC107", // Yellow border on top
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#20C4FC",
                justifyContent: "center",
                color: "#FFFFFF",
                borderRadius: "20px",
                px: 2,
                py: 1,
                width: "fit-content",
              }}
            >
              <Tooltip title="AI is creating a customized assessment to your interview role. We ensure that the assessment aligns with industry standards and the specific job responsibilities.">
                <InfoOutlined
                  sx={{ widht: 18, height: 18, color: "white", mt: 0.5 }}
                />
              </Tooltip>
              <Typography fontWeight={500} color="white">
                AI is creating customize assessment
              </Typography>
            </Box>
            {/* Robot Image */}
            <Box
              component="img"
              src={AIROBOT}
              alt="AI Robot"
              sx={{
                position: "absolute",
                top: 20,
                right: 20,
                width: 100,
                height: 100,
              }}
            />

            {/* Title */}
            <Typography variant="h6" fontWeight={600}>
              <Skeleton width={150} height={25} />
            </Typography>

            {/* Description */}
            <Box sx={{ my: 1 }}>
              <Skeleton variant="rectangular" width="100%" height={50} />
            </Box>

            {/* Tags and Info */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Skeleton variant="rectangular" width={80} height={20} />
              <Skeleton variant="rectangular" width={60} height={20} />
            </Box>

            {/* Buttons */}
            <Box
              sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton variant="rectangular" width={100} height={40} />
                <Skeleton variant="rectangular" width={100} height={40} />
              </Box>
              <Skeleton variant="rectangular" width={100} height={40} />
            </Box>
          </StyledCard>
        </Box>
      ) : (
        <StyledCard>
          <Box>
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Typography
                  fontSize="1.1rem"
                  sx={{
                    flex: 1,
                    fontWeight: 500,
                    display: "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                  }}
                >
                  {assessment?.assessment_name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {!assessment?.suggested ? (
                    <Box
                      component="img"
                      src={SUGGESTED}
                      width={90}
                      height={25}
                    />
                  ) : null}
                  {assessment?.company_id !== null && (
                    <Box bgcolor="#4BC7E1" borderRadius="20px" px={1} py={0.5}>
                      <Typography
                        fontFamily="Outfit"
                        color="white"
                        fontSize="12px"
                        fontWeight="500"
                        noWrap
                      >
                        My Assessment
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Tooltip title={assessment?.assessment_description || ""}>
                <Typography
                  fontSize="1rem"
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    display: "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                  }}
                >
                  {assessment?.assessment_description}
                </Typography>
              </Tooltip>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {assessment?.tests?.length ? (
                assessment?.tests?.slice(0, 2)?.map((tag) => (
                  <TagChip
                    key={tag?.id}
                    label={tag?.test_name}
                    size="small"
                    sx={{
                      backgroundColor: "#4BC7E1",
                      color: "#FFFFFF",
                      px: 0.5,
                      fontSize: "11px",
                    }}
                  />
                ))
              ) : (
                <Typography fontSize="13px">No Tests found !</Typography>
              )}
              <Tooltip
                title={
                  assessment?.tests?.length > 3 ? (
                    <Box>
                      {assessment?.tests?.slice(3).map((tag) => (
                        <TagChip
                          key={tag?.test_name}
                          label={tag?.test_name}
                          size="small"
                          sx={{
                            ml: 1,
                            maxWidth: "200px",
                            backgroundColor: "#4BC7E1",
                            color: "#FFFFFF",
                            px: 0.5,
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    ""
                  )
                }
              >
                {assessment?.tests?.length > 3 && (
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#37474F",
                      fontWeight: 600,
                      display: "inline-block",
                      marginLeft: "5px",
                      cursor: "pointer",
                    }}
                  >
                    +{assessment?.tests?.length - 3}
                  </Typography>
                )}
              </Tooltip>
              <TagChip
                label="Free"
                size="small"
                icon={<RedeemOutlined />}
                sx={{
                  backgroundColor: "#d1d1d1",
                  px: 0.5,
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              color: "text.secondary",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon fontSize="small" />
              <Typography fontSize=".7rem">
                {assessment?.assessment_time}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <QuizIcon fontSize="small" />
              <Typography fontSize=".7rem">
                {assessment?.questions_count} questions
              </Typography>
              <Box
                sx={{
                  width: "1px",
                  height: "12px",
                  backgroundColor: "#ccc",
                  ml: 1,
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontSize=".7rem">
                {assessment?.questions_category
                  ?.filter(Boolean)
                  .slice(0, 2)
                  .map(getCamelCaseWord)
                  .join(" - ")}
              </Typography>

              <Box
                sx={{
                  width: "1px",
                  height: "12px",
                  backgroundColor: "#ccc",
                }}
              />

              <Typography fontSize=".7rem">
                {getCamelCaseWord(assessment?.difficulty_level)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                bgcolor="#FFFFFF"
                styles={{
                  border: "2px solid #B7B7B7",
                  height: 35,
                }}
                textStyles={{
                  color: "#595959",
                }}
                onClick={() => handleOpenModal(assessment)}
              >
                Details
              </Button>

              {/* {isModal && ( */}
              <Button
                bgcolor="#FFFFFF"
                styles={{
                  border: "2px solid #B7B7B7",
                  height: 35,
                }}
                textStyles={{
                  color: "#595959",
                }}
                onClick={() => {
                  if (!isUserCompanySetup) {
                    setActionButtonToggle(true);
                    return;
                  }
                  navigate("/assessments/create", {
                    state: { assessment, edit: true },
                  });
                }}
              >
                Edit
              </Button>
              {/* )} */}
            </Box>
            <Button
              bgcolor={
                selectedAssessmentId === assessment?.id ? "#FFFFFF" : "#9048FB"
              }
              styles={{
                border:
                  selectedAssessmentId === assessment?.id
                    ? "2px solid #9048FB"
                    : "2px solid #B7B7B7",
                height: 35,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
              textStyles={{
                color:
                  selectedAssessmentId === assessment?.id
                    ? "#9048FB"
                    : "#FFFFFF",
              }}
              onClick={() => {
                console.log("assessment", assessment);
                handleAssessmentSelect(assessment);
              }}
            >
              {selectedAssessmentId === assessment?.id ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        selectedAssessmentId === assessment?.id
                          ? "#9048FB"
                          : "#FFFFFF",
                      fontWeight: 500,
                    }}
                  >
                    Selected
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{
                      border: "1px solid #9048FB",
                      height: 10,
                      width: 10,
                      p: 1,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              ) : (
                "Add"
              )}
            </Button>
          </Box>
        </StyledCard>
      )}
    </>
  );
};

const mapStateToProps = (state, props) => ({
  assessment: selectAssessmentById(state, props?.id),
  isUserCompanySetup: state.auth?.user?.company_id,
});

const mapDispatchToProps = {
  setActionButtonToggle: setActionButtonToggleAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssessmentsCards);
